import React from "react";
import PropTypes from "prop-types";
import { Color } from "three";
import DecipherComp from "./Types/Decipher";
import DgvComp from "./Types/DGV";
import NCBIComp from "./Types/NCBI";
import ClinvarCNVComp from "./Types/ClinvarCNV";
import ExacCNVComp from "./Types/ExacCNV";
import NIHGtexComp from "./Types/NIHGtex";
import BroadGnomadComp from "./Types/BroadGnomad";
import QueryLocationGraph from "../../../components/QueryLocationGraph";
import StructuralVariant from "../../../../../models/StructuralVariant";
import { colorScales } from "../../../constants/colors";
import Track from "../../../models/Track";
import CnvLink from "../../../components/TooltipActions/CnvLink";
import { DB_TO_CL } from "../../../constants/options";
import getBaseLen from "../../../helpers/getBaseLen";
import PathogenicityLabel from "../../../components/PathogenicityLabel";
import {
  dbNames,
  NIH_GTEX,
  BROAD_CNV,
  CLINVAR_CNV,
  SANGER_DECIPHER,
  CLINGEN_REGIONS,
  DGV,
  NCBI_DBVAR,
  GNOMAD_STRUCT,
  CLINGEN_CNV,
} from "../../../constants/dbs";
import ClingenRegions from "./Types/ClingenRegions";
import ClingenCnvComp from "./Types/ClingenCnv";

const components = {
  [dbNames[CLINVAR_CNV]]: ClinvarCNVComp,
  [dbNames[NCBI_DBVAR]]: NCBIComp,
  [dbNames[SANGER_DECIPHER]]: DecipherComp,
  [dbNames[DGV]]: DgvComp,
  [dbNames[NIH_GTEX]]: NIHGtexComp,
  [dbNames[BROAD_CNV]]: ExacCNVComp,
  [dbNames[GNOMAD_STRUCT]]: BroadGnomadComp,
  [dbNames[CLINGEN_REGIONS]]: ClingenRegions,
  [dbNames[CLINGEN_CNV]]: ClingenCnvComp,
};

function StructuralVariantsTitle({ item, actions, track }) {
  const [d1, d2] = track.stage.highlightedRegion;
  const confColorScale = DB_TO_CL[item.db?.category];
  const trackConf = track.getPreferences() || {};
  const colorTheme = trackConf[confColorScale];
  const colorScale = colorScales[colorTheme];
  const colScale = colorScale(item.db.categorySize);
  const srgbColor = colScale(item.db.categoryIndex);
  const queryGraphColor = `#${new Color(srgbColor).getHexString()}`;
  const CNVComp = components[item?.db?.name] || null;

  const tdCl = "tw-px-1 tw-border border-normal";
  const thCl = "tw-min-w-6 tw-px-1 tw-border border-normal tw-h-4 tw-relative";

  return (
    <>
      <div className="tooltip-header border-normal">
        <div className="tooltip-title">
          <CnvLink item={item} />
        </div>
        {actions}
      </div>
      <div className="color-3 tw-text-2xs">
        <table className="tw-w-full">
          <thead>
            <tr className="tw-bg-blueGray-400 tw-bg-opacity-20 tw-font-bold  tw-w-96">
              <th className={thCl}>
                <div
                  data-tooltip="Database"
                  data-tooltip-class="genome-browser-small-tooltip"
                  className="tw-absolute tw-inset-0 tw-px-1 tw-truncate"
                >
                  Database
                </div>
              </th>
              <th className={thCl}>
                <div
                  data-tooltip="Length"
                  data-tooltip-class="genome-browser-small-tooltip"
                  className="tw-absolute tw-inset-0 tw-px-1 tw-truncate"
                >
                  Length
                </div>
              </th>
              <th
                data-tooltip="Type"
                data-tooltip-class="genome-browser-small-tooltip"
                className={thCl}
              >
                <div className="tw-absolute tw-inset-0 tw-px-1 tw-truncate">
                  Type
                </div>
              </th>
              <th className={thCl}>
                <div
                  data-tooltip="Pathogenicity"
                  data-tooltip-class="genome-browser-small-tooltip"
                  className="tw-absolute tw-inset-0 tw-px-1 tw-truncate"
                >
                  Pathogenicity
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={tdCl}>
                {item.dbLink ? (
                  <a
                    href={item.dbLink}
                    target="_blank"
                    className="tw-cursor-pointer"
                    data-tooltip={`Open variant in ${item.db.name}`}
                    data-tooltip-class="genome-browser-small-tooltip"
                  >
                    {item.db.name}
                  </a>
                ) : (
                  <span>{item.db.name}</span>
                )}
              </td>
              <td className={tdCl}>{getBaseLen(item.region?.size)}</td>
              <td className={tdCl}>{item.type}</td>
              <td className={tdCl}>
                <PathogenicityLabel
                  pathoIndex={item.pathoIndex}
                  isLabelShort={true}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tw-h-2" />

      {CNVComp ? <CNVComp item={item} /> : null}

      <QueryLocationGraph
        item={item}
        start={d1}
        end={d2}
        region={item.region}
        fillColor={queryGraphColor}
        renderedByGenomeBrowser={true}
      />
    </>
  );
}

StructuralVariantsTitle.propTypes = {
  item: PropTypes.instanceOf(StructuralVariant).isRequired,
  track: PropTypes.instanceOf(Track).isRequired,
  actions: PropTypes.node,
};

StructuralVariantsTitle.defaultProps = {
  actions: null,
};

export default StructuralVariantsTitle;
